import {
    getProtectedOAuthBinding,
    hasProtectedOAuthBinding,
    useOAuthBindingDialogStore,
} from '@/features/auth/oauthBindingDialogStore';
import type { BindingState } from '@/lib/api/auth';
import { rebindOAuth } from '@/lib/api/auth';

/**
 * How long an unlink is allowed to be in flight.
 *
 * The guard is written the moment the owner transaction is SENT, before the
 * chain confirms it, so inside this window the server still reporting `bound`
 * proves nothing: the session simply has not seen the removal yet, and trading
 * authority stays revoked (fail closed).
 */
export const UNLINK_RECONCILIATION_MS = 10 * 60_000;

/**
 * Identities whose guard was verified against the chain in this page load and
 * turned out to be genuinely unlinked. Re-verifying them on every focus event
 * would cost one contract read per refresh for nothing.
 */
const verifiedUnbound = new Set<string>();

export function resetOAuthBindingGuardCache(): void {
    verifiedUnbound.clear();
}

interface OAuthBindingGuardDependencies {
    rebind: () => Promise<{ bindingState: BindingState }>;
    release: (accountHash: string) => void;
    nowMs: () => number;
}

const defaultDependencies: OAuthBindingGuardDependencies = {
    rebind: async () => rebindOAuth(),
    release: (accountHash) =>
        useOAuthBindingDialogStore.getState().release(accountHash),
    nowMs: Date.now,
};

/**
 * Whether the local unlink guard still revokes this identity's OAuth trading
 * authority.
 *
 * The guard exists so that an owner who just unlinked cannot keep trading (and
 * cannot be auto-selected again) while the removal reconciles. It was never
 * meant to outlive the unlink: written optimistically and only released by the
 * flow that wrote it, an abandoned unlink left a localStorage entry that
 * stripped trading authority on every single load, forever, with no way back.
 *
 * So a guard older than the reconciliation window no longer gets to assert
 * anything on its own. `GET /v2/auth/me` cannot settle it either — it
 * short-circuits on an already-`bound` session and never re-reads the chain —
 * so the answer comes from `POST /v2/auth/rebind`, which does. Still bound
 * means the unlink never landed: the guard is released and the user keeps the
 * session they are in. Anything else keeps authority revoked.
 */
export async function isOAuthAuthorityRevokedByGuard(
    accountHash: string | null | undefined,
    bindingState: BindingState | null | undefined,
    dependencies: OAuthBindingGuardDependencies = defaultDependencies,
): Promise<boolean> {
    if (!accountHash) {
        // an identity we cannot name cannot be cleared of a guard either
        return hasProtectedOAuthBinding();
    }

    const protection = getProtectedOAuthBinding(accountHash);
    if (!protection) return false;

    const { protectedAtMs } = protection;
    if (
        protectedAtMs !== null &&
        dependencies.nowMs() - protectedAtMs < UNLINK_RECONCILIATION_MS
    ) {
        return true;
    }

    if (bindingState !== 'bound') return true;
    if (verifiedUnbound.has(accountHash.toLowerCase())) return true;

    try {
        const result = await dependencies.rebind();
        if (result.bindingState === 'bound') {
            dependencies.release(accountHash);
            return false;
        }
        verifiedUnbound.add(accountHash.toLowerCase());
        return true;
    } catch {
        // an unreachable re-check is not permission to trade
        return true;
    }
}
